/**
 * model.js — pure local element store + delta-apply reducer.
 *
 * The store is a plain object { byId: { [element_id]: element } }.
 * All functions return new store objects (immutable-style); no mutation in place.
 *
 * API:
 *   createStore(elements?)           → store
 *   applyChanges(store, changes, fetchedById) → { store, needsFetch }
 *   mergePage(store, elements)        → store
 *   diffIds(store, serverIds)         → { deletedLocally, changedOrNew }
 */

/**
 * Create a new empty store, optionally pre-populated with an element array.
 * Deep-copies elements so callers cannot accidentally mutate the store.
 *
 * @param {Array} [elements=[]] - Optional initial element array
 * @returns {{ byId: Object }}
 */
export function createStore(elements = []) {
  const byId = {};
  for (const el of elements) {
    byId[el.element_id] = JSON.parse(JSON.stringify(el));
  }
  return { byId };
}

/**
 * Apply a changes array (upsert/delete entries from the server) to the store.
 *
 * Rules:
 *   delete  — remove the element. If it is a vertex, also remove all incident edges.
 *   upsert  — if fetchedById has the element, replace store's copy (unless store already
 *             has that exact rev — skip stale/self). If NOT in fetchedById and store
 *             doesn't have it at that rev, add element_id to needsFetch.
 *
 * Returns a new store (original not mutated) and the set of ids that still need fetching.
 *
 * @param {{ byId: Object }} store
 * @param {Array} changes  - [{ op: 'upsert'|'delete', element_id, type, rev }]
 * @param {Object} fetchedById - { [element_id]: element } — already-fetched data
 * @returns {{ store: { byId: Object }, needsFetch: string[] }}
 */
export function applyChanges(store, changes, fetchedById) {
  // Shallow-copy byId so we don't mutate the original.
  const byId = { ...store.byId };
  const needsFetch = [];

  for (const change of changes) {
    const { op, element_id, type, rev } = change;

    if (op === 'delete') {
      if (type === 'vertex') {
        // Remove incident edges first
        for (const id of Object.keys(byId)) {
          const el = byId[id];
          if (el.type === 'edge' && (el.from_id === element_id || el.to_id === element_id)) {
            delete byId[id];
          }
        }
      }
      delete byId[element_id];
    } else if (op === 'upsert') {
      const existing = byId[element_id];
      const alreadyAtRev = existing && existing.rev === rev;

      if (alreadyAtRev) {
        // Skip: store already has this exact rev (own optimistic edit or duplicate event)
        continue;
      }

      if (fetchedById && fetchedById[element_id] !== undefined) {
        // We have the fetched copy — apply it
        byId[element_id] = JSON.parse(JSON.stringify(fetchedById[element_id]));
      } else {
        // Need to fetch this id
        needsFetch.push(element_id);
      }
    }
  }

  return { store: { byId }, needsFetch };
}

/**
 * Merge a page of elements from listSessionGraphElements into the store.
 * Replaces an existing element only if the incoming rev is strictly greater.
 *
 * @param {{ byId: Object }} store
 * @param {Array} elements
 * @returns {{ byId: Object }}
 */
export function mergePage(store, elements) {
  const byId = { ...store.byId };

  for (const el of elements) {
    const existing = byId[el.element_id];
    if (!existing || el.rev > existing.rev) {
      byId[el.element_id] = JSON.parse(JSON.stringify(el));
    }
  }

  return { byId };
}

/**
 * Diff the local store against a server-supplied id list (from ids_only list).
 *
 * deletedLocally  — ids present in the store but absent from the server list
 *                   (the server has removed them; we should drop them locally).
 * changedOrNew    — ids in the server list whose rev differs from the store, or
 *                   that aren't in the store at all (need to fetch the full element).
 *
 * @param {{ byId: Object }} store
 * @param {Array} serverIds - [{ element_id, rev, type }]
 * @returns {{ deletedLocally: string[], changedOrNew: string[] }}
 */
export function diffIds(store, serverIds) {
  const serverMap = {};
  for (const entry of serverIds) {
    serverMap[entry.element_id] = entry.rev;
  }

  const deletedLocally = [];
  for (const id of Object.keys(store.byId)) {
    if (!(id in serverMap)) {
      deletedLocally.push(id);
    }
  }

  const changedOrNew = [];
  for (const entry of serverIds) {
    const local = store.byId[entry.element_id];
    if (!local || local.rev !== entry.rev) {
      changedOrNew.push(entry.element_id);
    }
  }

  return { deletedLocally, changedOrNew };
}

/**
 * Subset-mode frontier test: can this vertex still be expanded (may have unloaded neighbors)?
 *
 * A vertex counts as fully loaded only once it has been expanded to exhaustion — its neighbor
 * cursor is recorded and null. A vertex with no recorded cursor (it arrived as another vertex's
 * neighbor and was never expanded) or a non-null cursor (more neighbor pages remain) is a frontier
 * vertex that can still be traversed.
 *
 * This is a best-effort, local snapshot of what THIS client has loaded; it does not know about
 * neighbors added on the server after a vertex was exhausted (re-expanding refreshes that).
 *
 * @param {Object} neighborCursors - { [element_id]: cursor | null }
 * @param {string} elementId
 * @returns {boolean} true if the vertex is a frontier (still expandable)
 */
export function vertexNeedsExpand(neighborCursors, elementId) {
  const cursors = neighborCursors || {};
  if (!Object.prototype.hasOwnProperty.call(cursors, elementId)) return true;
  return cursors[elementId] !== null;
}
